import { useEffect } from 'react'
import type { AdTierConfig } from '../components/Ads/adTierConfigs'

const gptGlobalSetupComplete = false

// Page-level targeting, applied once and shared by every ad container.
const PAGE_TARGETING: Record<string, string | string[]> = {
  app_mode: 'prod',
  page_type: 'Static',
  page_group_new: 'retirement',
  page_key: 'sa.retirement.fn.financiala'
}

/**
 * Wires up Google Publisher Tag key-value targeting for an ad container:
 * page-level targeting once on mount, then this tier's targeting plus any
 * dynamic targeting — clearing those same keys on cleanup so they don't leak
 * across tier changes.
 */
export const useAdTargeting = (
  currentTier: AdTierConfig | undefined,
  dynamicTargeting: Record<string, string | string[]> = {}
) => {
  // Page-level settings, applied once.
  useEffect(() => {
    if (!gptGlobalSetupComplete && window.googletag) {
      window.googletag.cmd.push(() => {
        window.googletag!.setConfig({ targeting: { ...PAGE_TARGETING } })
      })
    }
  }, [])

  // Memoize the dynamic targeting to avoid JSON.stringify in deps
  const dynamicTargetingStr = JSON.stringify(dynamicTargeting)

  // Apply this tier's targeting plus any dynamic targeting, and clear those
  // same keys on cleanup so they don't leak across tier changes.
  useEffect(() => {
    const allTargetingToApply = {
      ...(currentTier?.tierTargeting || {}),
      ...dynamicTargeting
    }

    if (window.googletag && Object.keys(allTargetingToApply).length > 0) {
      window.googletag.cmd.push(() => {
        window.googletag!.setConfig({ targeting: allTargetingToApply })
      })
    }

    return () => {
      if (window.googletag && Object.keys(allTargetingToApply).length > 0) {
        window.googletag.cmd.push(() => {
          const clearedTargeting = Object.fromEntries(
            Object.keys(allTargetingToApply).map(key => [key, null])
          )
          window.googletag!.setConfig({ targeting: clearedTargeting })
        })
      }
    }
  }, [currentTier, dynamicTargeting, dynamicTargetingStr])
}
